/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
import { useState, useCallback, useEffect, useRef, ChangeEvent, MouseEvent, FC, MutableRefObject } from 'react';

import Dropdown from './Controls/Dropdown';
import Playback from './Controls/Playback';
import Progress from './Controls/Progress';
import Rewind from './Controls/Rewind';
import Skip from './Controls/Skip';
import Time from './Controls/Time';
import { useLocalStorage } from './hooks/storage-hook';
import { useTimeout } from './hooks/timer-hook';
import Error from './UI/Error/Error';
import KeyAction, { KeyActionHandle } from './UI/KeyAction/KeyAction';
import Loader from './UI/Loader/Loader';
import { formatTime } from './util/format';

import './VideoPlayer.css';
import { RESULT } from '@/commons/data';
import { cn } from '@/lib/utils';

interface VideoPlayerProps {
    src: string;
    autoPlay?: boolean;
    videoPlayerRef: (node: HTMLVideoElement) => void;
    chapters: { time: number; result: RESULT }[];
    className?: string;
    videoPlayerClassName?: string;
    onError?: () => void;
    handleSeek?: (target: HTMLVideoElement) => void;
}

const VideoPlayer: FC<VideoPlayerProps> = ({
    src,
    autoPlay = true,
    chapters,
    videoPlayerRef,
    className,
    videoPlayerClassName,
    onError = () => {},
    handleSeek = () => {},
}) => {
    const [playbackState, setPlaybackState] = useState(false);
    const [currentProgress, setCurrentProgress] = useState(0);
    const [bufferProgress, setBufferProgress] = useState(0);
    const [seekProgress, setSeekProgress] = useState(0);
    const [videoDuration, setVideoDuration] = useState(0);
    const [seekTooltip, setSeekTooltip] = useState('00:00');
    const [seekTooltipPosition, setSeekTooltipPosition] = useState('');
    const [currentTimeUI, setCurrentTimeUI] = useState('00:00');
    const [remainedTimeUI, setRemainedTimeUI] = useState('00:00');
    // TODO: We'll need this later
    const [fullscreenState, setFullscreenState] = useState(false);
    const [displayDropdown, setDisplayDropdown] = useState(false);
    const [playbackRates] = useState([0.5, 0.75, 1, 1.25, 1.5]);
    const [activePlaybackRate, setActivePlaybackRate] = useLocalStorage('video-playbackrate', 1);
    const [displayLoader, setDisplayLoader] = useState(true);
    const [volumeKeyAction, setvolumeKeyAction] = useState(false);
    const [videoError, setVideoError] = useState<MediaError | null>(null);

    const videoRef: MutableRefObject<HTMLVideoElement | null> = useRef<HTMLVideoElement | null>(null);
    const videoContainerRef = useRef<HTMLDivElement>(null);
    const videoKeyActionRef = useRef<KeyActionHandle>(null);

    const playPromise = useRef<Promise<void>>();
    const progressSeekData = useRef(0);

    const [setKeyActionVolumeTimeout] = useTimeout();
    const [setLoaderTimeout, clearLoaderTimeout] = useTimeout();

    /**
     * PLAYBACK
     */

    const togglePlayHandler = useCallback(() => {
        const video = videoRef.current!;

        if (video.paused || video.ended) {
            playPromise.current = video.play();
            return;
        }

        if (!playPromise.current) {
            return;
        }

        playPromise.current.then(() => {
            video.pause();
        });
    }, []);

    const videoPlayHandler = useCallback(() => {
        setPlaybackState(true);
    }, []);

    const videoPauseHandler = useCallback(() => {
        setPlaybackState(false);
    }, []);

    /**
     * LOADING
     */

    const showLoaderHandler = useCallback(() => {
        setLoaderTimeout(() => setDisplayLoader(true), 300);
    }, [setLoaderTimeout]);

    const hideLoaderHandler = useCallback(() => {
        clearLoaderTimeout();
        setDisplayLoader(false);
    }, [clearLoaderTimeout]);

    /**
     * TIME
     */

    const timeChangeHandler = useCallback(() => {
        const video = videoRef.current!;

        handleSeek?.(video);

        const duration = video.duration || 0;
        const currentTime = video.currentTime || 0;
        const buffer = video.buffered;

        // Progress
        setCurrentProgress((currentTime / duration) * 100);
        setSeekProgress(currentTime);

        if (duration > 0) {
            for (let i = 0; i < buffer.length; ++i) {
                if (
                    buffer.start(buffer.length - 1 - i) === 0 ||
                    buffer.start(buffer.length - 1 - i) < video.currentTime
                ) {
                    setBufferProgress((buffer.end(buffer.length - 1 - i) / duration) * 100);
                    break;
                }
            }
        }

        // Time
        const formattedCurrentTime = formatTime(Math.round(currentTime));
        const formattedRemainedTime = formatTime(Math.round(duration) - Math.round(currentTime));

        setCurrentTimeUI(formattedCurrentTime);
        setRemainedTimeUI(formattedRemainedTime);
    }, [handleSeek]);

    /**
     * SEEK
     */

    const seekMouseMoveHandler = useCallback((event: MouseEvent) => {
        const video = videoRef.current!;

        const rect = event.currentTarget.getBoundingClientRect();
        const skipTo = (event.nativeEvent.offsetX / rect.width) * video.duration;

        progressSeekData.current = skipTo;

        let formattedTime: string;

        if (skipTo > video.duration) {
            formattedTime = formatTime(video.duration);
        } else if (skipTo < 0) {
            formattedTime = '00:00';
        } else {
            formattedTime = formatTime(skipTo);
            setSeekTooltipPosition(`${event.nativeEvent.offsetX}px`);
        }

        setSeekTooltip(formattedTime);
    }, []);

    const seekInputHandler = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        const video = videoRef.current!;

        const skipTo = progressSeekData.current || +event.target.value;

        video.currentTime = skipTo;
        setCurrentProgress((skipTo / video.duration) * 100);
        setSeekProgress(skipTo);
    }, []);

    /**
     * REWIND & SKIP
     */

    const rewindHandler = useCallback(() => {
        const video = videoRef.current!;

        video.currentTime -= 10;

        const rewindContainer = videoKeyActionRef.current!.rewind;
        const rewindElement = rewindContainer.firstElementChild as HTMLElement;

        rewindContainer.animate([{ opacity: 0 }, { opacity: 1 }, { opacity: 1 }, { opacity: 0 }], {
            duration: 1000,
            easing: 'ease-out',
            fill: 'forwards',
        });
        rewindElement.animate(
            [
                { opacity: 1, transform: 'translateX(0)' },
                { opacity: 0, transform: `translateX(-20%)` },
            ],
            {
                duration: 1000,
                easing: 'ease-in-out',
                fill: 'forwards',
            },
        );
    }, []);

    const skipHandler = useCallback(() => {
        const video = videoRef.current!;

        video.currentTime += 10;

        const forwardContainer = videoKeyActionRef.current!.skip;
        const forwardElement = forwardContainer.firstElementChild as HTMLElement;

        forwardContainer.animate([{ opacity: 0 }, { opacity: 1 }, { opacity: 1 }, { opacity: 0 }], {
            duration: 1000,
            easing: 'ease-out',
            fill: 'forwards',
        });
        forwardElement.animate(
            [
                { opacity: 1, transform: 'translateX(0)' },
                { opacity: 0, transform: `translateX(20%)` },
            ],
            {
                duration: 1000,
                easing: 'ease-in-out',
                fill: 'forwards',
            },
        );
    }, []);

    /**
     * FULLSCREEN
     */

    const toggleFullscreenHandler = useCallback(() => {
        if (document.fullscreenElement) {
            document.exitFullscreen();
        } else {
            videoContainerRef.current!.requestFullscreen();
        }
    }, []);

    const fullscreenChangeHandler = useCallback(() => {
        if (document.fullscreenElement) {
            setFullscreenState(true);
        } else {
            setFullscreenState(false);
        }
    }, []);

    /**
     * SETTINGS
     */

    const changePlaybackRateHandler = useCallback(
        (playbackRate: number) => {
            const video = videoRef.current!;

            video.playbackRate = playbackRate;
            setActivePlaybackRate(playbackRate);
        },
        [setActivePlaybackRate],
    );

    /**
     * KEYBOARD SHORTKUTS
     */

    const keyEventHandler = useCallback(
        (event: KeyboardEvent) => {
            const video = videoRef.current!;
            const activeElement = document.activeElement;

            if (
                !activeElement ||
                (activeElement.localName === 'input' && (activeElement as HTMLInputElement).type !== 'range') ||
                activeElement.localName === 'textarea'
            ) {
                return;
            }

            const { key } = event;

            switch (key) {
                case 'ArrowLeft':
                    event.preventDefault();
                    rewindHandler();
                    break;
                case 'ArrowRight':
                    event.preventDefault();
                    skipHandler();
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    if (video.volume + 0.05 > 1) {
                        video.volume = 1;
                    } else {
                        video.volume = +(video.volume + 0.05).toFixed(2);
                    }

                    setvolumeKeyAction(true);
                    setKeyActionVolumeTimeout(() => {
                        setvolumeKeyAction(false);
                    }, 1500);

                    break;
                case 'ArrowDown':
                    event.preventDefault();
                    if (video.volume - 0.05 < 0) {
                        video.volume = 0;
                    } else {
                        video.volume = +(video.volume - 0.05).toFixed(2);
                    }

                    setvolumeKeyAction(true);
                    setKeyActionVolumeTimeout(() => {
                        setvolumeKeyAction(false);
                    }, 1500);

                    break;
                case ' ':
                    event.preventDefault();
                    togglePlayHandler();
                    break;
            }
        },
        [togglePlayHandler, rewindHandler, skipHandler, setKeyActionVolumeTimeout],
    );

    /**
     * LOAD VIDEO
     */

    const videoLoadedHandler = useCallback(() => {
        const video = videoRef.current!;

        video.playbackRate = activePlaybackRate;

        setVideoDuration(video.duration);
        timeChangeHandler();

        document.addEventListener('keydown', keyEventHandler);
        document.addEventListener('fullscreenchange', fullscreenChangeHandler);

        autoPlay && (playPromise.current = video.play());
    }, [autoPlay, activePlaybackRate, timeChangeHandler, keyEventHandler, fullscreenChangeHandler, src, handleSeek]);

    /**
     * ERROR HANDLER
     */

    const errorHandler = useCallback(() => {
        onError();
    }, []);

    /**
     * INITIATE PLAYER
     */

    useEffect(() => {
        return () => {
            document.removeEventListener('fullscreenchange', fullscreenChangeHandler);
            document.removeEventListener('keydown', keyEventHandler);
        };
    }, [fullscreenChangeHandler, keyEventHandler]);

    /**
     * RENDER
     */

    return (
        <div className={cn('vp-container', className)} ref={videoContainerRef}>
            <video
                muted
                ref={(node: HTMLVideoElement) => {
                    videoRef.current = node;
                    videoPlayerRef(node);
                }}
                src={src}
                controls={false}
                onLoadedMetadata={videoLoadedHandler}
                onClick={togglePlayHandler}
                onPlay={videoPlayHandler}
                onPause={videoPauseHandler}
                onTimeUpdate={timeChangeHandler}
                onDoubleClick={toggleFullscreenHandler}
                onSeeking={showLoaderHandler}
                onSeeked={hideLoaderHandler}
                onWaiting={showLoaderHandler}
                onCanPlay={hideLoaderHandler}
                onError={errorHandler}
                className={cn('vp-video', videoPlayerClassName)}
            />
            <Loader on={displayLoader} />
            <KeyAction ref={videoKeyActionRef} on={volumeKeyAction} />
            <Error error={videoError} />
            <div className="vp-controls">
                <Dropdown
                    on={displayDropdown}
                    playbackRates={playbackRates}
                    activePlaybackRate={activePlaybackRate}
                    onClose={setDisplayDropdown}
                    onChangePlaybackRate={changePlaybackRateHandler}
                />
                <div className="vp-controls__header">
                    <Time time={currentTimeUI} />
                    <Progress
                        bufferProgress={bufferProgress}
                        currentProgress={currentProgress}
                        videoDuration={videoDuration}
                        seekProgress={seekProgress}
                        seekTooltip={seekTooltip}
                        seekTooltipPosition={seekTooltipPosition}
                        onHover={seekMouseMoveHandler}
                        onSeek={seekInputHandler}
                        chapters={chapters}
                    />
                    <Time time={remainedTimeUI} />
                </div>
                <div className="vp-controls__body">
                    <div>
                        <Rewind onRewind={rewindHandler} />
                        <Playback isPlaying={playbackState} onToggle={togglePlayHandler} />
                        <Skip onSkip={skipHandler} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VideoPlayer;
